import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import fileDownload from 'js-file-download';

import type { ResourceIdDto } from '@/api/generated';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';

type Req = {
  externalDiskId: string;
  externalFileId: string;
  resource: ResourceIdDto;
};

type Return = {
  downloadFile: (req: Req) => Promise<void>;
  isDownloading: boolean;
};

export const useDownloadFile = (): Return => {
  const { attachmentsApi } = useApiClient();
  const { userId } = useUserContext();

  const { isPending, mutateAsync } = useMutation<void, AxiosError, Req>({
    mutationFn: async req => {
      const response = await attachmentsApi.downloadAttachment(
        userId,
        req.resource.resourceId,
        req.resource.type,
        req.externalFileId,
        req.externalDiskId,
        {
          responseType: 'blob',
        },
      );
      const header = response.headers['content-disposition'] as string;
      const match = header.match(/attachment; filename=(.*)/);
      const fileName = match ? match[1] : `file_${dayjs().toISOString()}`;
      fileDownload(response.data, fileName);
    },
  });

  return {
    downloadFile: mutateAsync,
    isDownloading: isPending,
  };
};
